import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { LayoutDashboard, Users, Palette, LogOut, ShieldCheck } from "lucide-react";

const NAV = [
  { to: "/admin", label: "Overview", icon: LayoutDashboard, testid: "nav-admin-overview", end: true },
  { to: "/admin/dietitians", label: "Dietitians", icon: Users, testid: "nav-admin-dietitians" },
  { to: "/admin/branding", label: "Branding", icon: Palette, testid: "nav-admin-branding" },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const handleLogout = async () => {
    await logout();
    navigate("/dietitian/login", { replace: true });
  };

  return (
    <div className="min-h-screen flex bg-background" data-testid="admin-layout">
      <aside className="hidden lg:flex flex-col w-64 border-r border-border bg-card sticky top-0 h-screen">
        <div className="p-6 border-b border-border">
          <Logo size="md" />
          <div className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold px-2 py-1 rounded-full bg-secondary text-secondary-foreground">
            <ShieldCheck size={12} /> Super Admin
          </div>
        </div>
        <nav className="flex-1 p-4 space-y-1">
          {NAV.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                data-testid={item.testid}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                    isActive ? "bg-secondary text-secondary-foreground" : "text-muted-foreground hover:bg-muted hover:text-foreground"
                  }`
                }
              >
                <Icon size={18} />
                {item.label}
              </NavLink>
            );
          })}
        </nav>
        <div className="p-4 border-t border-border">
          <div className="px-2 mb-3">
            <div className="text-sm font-semibold text-foreground" data-testid="admin-user-name">{user?.name}</div>
            <div className="text-xs text-muted-foreground truncate">{user?.email}</div>
          </div>
          <Button variant="outline" className="w-full justify-start gap-2" onClick={handleLogout} data-testid="admin-logout-button">
            <LogOut size={16} /> Log out
          </Button>
        </div>
      </aside>

      <div className="lg:hidden fixed top-0 left-0 right-0 z-30 bg-card border-b border-border flex items-center justify-between px-4 py-3">
        <Logo size="sm" />
        <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="admin-mobile-logout">
          <LogOut size={16} />
        </Button>
      </div>

      <main className="flex-1 pt-16 lg:pt-0 pb-20 lg:pb-0">
        <div className="max-w-6xl mx-auto px-6 py-8">
          <Outlet />
        </div>
      </main>

      <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-30 bg-card border-t border-border flex items-center justify-around py-2">
        {NAV.map((item) => {
          const Icon = item.icon;
          return (
            <NavLink key={item.to} to={item.to} end={item.end}
              className={({ isActive }) => `flex flex-col items-center gap-1 px-3 py-2 text-xs ${isActive ? "text-primary" : "text-muted-foreground"}`}
              data-testid={`mobile-${item.testid}`}
            >
              <Icon size={18} /> <span>{item.label}</span>
            </NavLink>
          );
        })}
      </nav>
    </div>
  );
}
